<script lang="ts" setup>
import { onMounted, reactive, ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { AccountDetails } from '@/types'
import { useAxios } from '@/composables/useAxios'
import { handleAxiosError, getErrorMessage } from '@/composables/useAxiosError'
import { useNotifyStore } from '@/stores'
import { uuid } from '@/composables'
import { STATUS_CODES } from '@/constants'
import BaseDashboardPage from '@/components/base/BaseDashboardPage.vue'
import { DashboardLoadingState, DashboardReasonConfirmDialog } from '@/components/dashboard/common'
import UserEdit from './UserEdit.vue'
import UserSessionsSection from './UserSessionsSection.vue'
import UserRolesSection from './UserRolesSection.vue'
import UserSubAccountsSection from './UserSubAccountsSection.vue'

const { t } = useI18n()
const router = useRouter()
const notifyStore = useNotifyStore()

type TabType = 'overview' | 'sessions' | 'roles' | 'subAccounts'
const activeTab = ref<TabType>('overview')
const editUser = ref<AccountDetails | null>(null)

const data = reactive({
  loading: false,
  account: null as AccountDetails | null
})

const deactivateDialog = reactive({
  show: false,
  loading: false
})

const accountId = computed(() => {
  return Number(router.currentRoute.value.params.id)
})

const displayName = computed(() => {
  if (!data.account) return ''
  if (data.account.account_type === 'organization' && data.account.org) {
    return data.account.org
  }
  return [data.account.name, data.account.surname].filter(Boolean).join(' ')
})

function loadAccount() {
  data.loading = true
  useAxios<null, AccountDetails>('GET', `/account/${accountId.value}`, true)
    .then((res) => {
      if (res?.status === STATUS_CODES.OK && res.data) {
        const validate = AccountDetails.safeParse(res.data)
        if (validate.success) {
          data.account = validate.data
        }
      }
    })
    .catch((error) => {
      handleAxiosError(error, { logPrefix: 'UserDetailPage:load' })
    })
    .finally(() => {
      data.loading = false
    })
}

function goBack() {
  router.push({ name: 'dashboardAdminUserManagement' })
}

function openEdit() {
  if (data.account) {
    editUser.value = data.account
  }
}

function handleCloseEdit() {
  editUser.value = null
}

function handleSaved() {
  editUser.value = null
  loadAccount()
}

// Backend refuses admin targets anyway (403); hide the action for admins,
// mirroring the impersonate-button precedent in UserTable.
const canDeactivate = computed(() => {
  return !!data.account && data.account.active === true && data.account.role !== 'admin'
})

async function confirmDeactivate(reason: string) {
  deactivateDialog.loading = true

  try {
    const res = await useAxios<{ reason: string }, { status?: string; message?: string }>(
      'POST',
      `/admin/account/${accountId.value}/deactivate`,
      true,
      { reason }
    )

    if (res && res.status === STATUS_CODES.OK) {
      notifyStore.createNotify({
        id: uuid('v4'),
        alertType: 'success',
        title: t('dashboard.admin.users.deactivate.success'),
        message: data.account?.email ?? ''
      })
      deactivateDialog.show = false
      loadAccount()
    }
  } catch (error) {
    handleAxiosError(error as Error, { logPrefix: 'UserDetailPage:deactivate' })
    notifyStore.createNotify({
      id: uuid('v4'),
      alertType: 'error',
      title: t('common.error'),
      message: getErrorMessage(error as Error)
    })
  } finally {
    deactivateDialog.loading = false
  }
}

onMounted(() => {
  loadAccount()
})
</script>

<template>
  <BaseDashboardPage
    :title="displayName || `User #${accountId}`"
    :subtitle="data.account?.email ?? ''"
    icon="mdi-account"
    icon-color="cyan"
  >
    <template #actions>
      <button
        v-if="canDeactivate"
        class="deactivate-btn"
        @click="deactivateDialog.show = true"
      >
        <v-icon icon="mdi-account-off" size="18" />
        <span>{{ $t('dashboard.admin.users.deactivate.button') }}</span>
      </button>
      <button class="back-btn" @click="goBack">
        <v-icon icon="mdi-arrow-left" size="18" />
        <span>{{ $t('dashboard.admin.users.detail.back') }}</span>
      </button>
    </template>

    <!-- Loading -->
    <DashboardLoadingState v-if="data.loading" />

    <template v-else-if="data.account">
      <!-- Status badges -->
      <div class="user-badges">
        <span class="badge" :class="data.account.active ? 'badge-active' : 'badge-inactive'">
          {{ data.account.active ? $t('dashboard.admin.users.edit.active') : $t('dashboard.admin.users.edit.inactive') }}
        </span>
        <span v-if="data.account.email_verified" class="badge badge-verified">
          <v-icon icon="mdi-check-circle" size="14" />
          {{ $t('dashboard.admin.users.emailVerified') }}
        </span>
        <span v-if="data.account.totp" class="badge badge-2fa">
          <v-icon icon="mdi-shield-lock" size="14" />
          {{ $t('dashboard.admin.users.edit.twoFactorActive') }}
        </span>
        <span class="badge badge-type">
          {{ data.account.role }}
        </span>
      </div>

      <!-- Tabs -->
      <div class="tabs-container">
        <button
          class="tab-btn"
          :class="{ active: activeTab === 'overview' }"
          @click="activeTab = 'overview'"
        >
          <v-icon icon="mdi-account-edit" size="18" />
          <span>{{ $t('dashboard.admin.users.detail.tabs.overview') }}</span>
        </button>
        <button
          class="tab-btn"
          :class="{ active: activeTab === 'sessions' }"
          @click="activeTab = 'sessions'"
        >
          <v-icon icon="mdi-devices" size="18" />
          <span>{{ $t('dashboard.admin.users.detail.tabs.sessions') }}</span>
        </button>
        <button
          class="tab-btn"
          :class="{ active: activeTab === 'roles' }"
          @click="activeTab = 'roles'"
        >
          <v-icon icon="mdi-shield-account" size="18" />
          <span>{{ $t('dashboard.admin.users.detail.tabs.roles') }}</span>
        </button>
        <button
          class="tab-btn"
          :class="{ active: activeTab === 'subAccounts' }"
          @click="activeTab = 'subAccounts'"
        >
          <v-icon icon="mdi-account-group" size="18" />
          <span>{{ $t('dashboard.admin.users.detail.tabs.subAccounts') }}</span>
        </button>
      </div>

      <!-- Tab Content -->
      <div class="tab-content">
        <!-- Overview Tab -->
        <div v-if="activeTab === 'overview'" class="tab-panel">
          <div class="overview-card">
            <div class="overview-header">
              <h3 class="overview-title">{{ $t('dashboard.admin.users.edit.editUserData') }}</h3>
              <button class="edit-btn" @click="openEdit">
                <v-icon icon="mdi-pencil" size="16" />
                <span>{{ $t('dashboard.admin.users.table.edit') }}</span>
              </button>
            </div>

            <div class="info-grid">
              <div class="info-group">
                <span class="info-label">{{ $t('dashboard.admin.users.edit.email') }}</span>
                <span class="info-value">{{ data.account.email }}</span>
              </div>
              <div class="info-group">
                <span class="info-label">{{ $t('dashboard.admin.users.edit.phone') }}</span>
                <span class="info-value">{{ data.account.phone || '—' }}</span>
              </div>
              <div v-if="data.account.account_type === 'organization'" class="info-group">
                <span class="info-label">{{ $t('dashboard.admin.users.edit.companyName') }}</span>
                <span class="info-value">{{ data.account.org || '—' }}</span>
              </div>
              <div class="info-group">
                <span class="info-label">{{ $t('dashboard.admin.users.edit.firstName') }}</span>
                <span class="info-value">{{ data.account.name || '—' }}</span>
              </div>
              <div class="info-group">
                <span class="info-label">{{ $t('dashboard.admin.users.edit.lastName') }}</span>
                <span class="info-value">{{ data.account.surname || '—' }}</span>
              </div>
              <div class="info-group">
                <span class="info-label">{{ $t('dashboard.admin.users.edit.accountType') }}</span>
                <span class="info-value info-capitalize">{{ data.account.account_type }}</span>
              </div>
              <div class="info-group">
                <span class="info-label">{{ $t('dashboard.admin.users.edit.role') }}</span>
                <span class="info-value info-capitalize">{{ data.account.role }}</span>
              </div>
              <div class="info-group">
                <span class="info-label">{{ $t('dashboard.admin.users.edit.street') }}</span>
                <span class="info-value">{{ data.account.line1 || '—' }}</span>
              </div>
              <div class="info-group">
                <span class="info-label">{{ $t('dashboard.admin.users.edit.city') }}</span>
                <span class="info-value">{{ [data.account.postcode, data.account.city].filter(Boolean).join(' ') || '—' }}</span>
              </div>
              <div class="info-group">
                <span class="info-label">{{ $t('dashboard.admin.users.edit.country') }}</span>
                <span class="info-value">{{ data.account.country_iso || '—' }}</span>
              </div>
              <div v-if="data.account.ust_id" class="info-group">
                <span class="info-label">{{ $t('dashboard.admin.users.edit.vatId') }}</span>
                <span class="info-value">{{ data.account.ust_id }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Sessions Tab -->
        <div v-if="activeTab === 'sessions'" class="tab-panel">
          <UserSessionsSection :account-id="accountId" />
        </div>

        <!-- Roles Tab -->
        <div v-if="activeTab === 'roles'" class="tab-panel">
          <UserRolesSection :account-id="accountId" />
        </div>

        <!-- Sub-Accounts Tab -->
        <div v-if="activeTab === 'subAccounts'" class="tab-panel">
          <UserSubAccountsSection :account-id="accountId" />
        </div>
      </div>
    </template>

    <!-- Edit Slider (reuses existing UserEdit as slider) -->
    <UserEdit
      :user="editUser"
      mode="edit"
      @close="handleCloseEdit"
      @saved="handleSaved"
    />

    <!-- Deactivate Confirmation Dialog -->
    <DashboardReasonConfirmDialog
      v-model="deactivateDialog.show"
      :title="$t('dashboard.admin.users.deactivate.dialogTitle')"
      :subtitle="data.account?.email"
      :description="$t('dashboard.admin.users.deactivate.dialogMessage')"
      :warning="$t('dashboard.admin.users.deactivate.warning')"
      icon="mdi-account-off"
      variant="danger"
      :confirm-text="$t('dashboard.admin.users.deactivate.confirm')"
      confirm-icon="mdi-account-off"
      :loading="deactivateDialog.loading"
      :reason-label="$t('dashboard.admin.users.deactivate.reasonLabel')"
      :reason-placeholder="$t('dashboard.admin.users.deactivate.reasonPlaceholder')"
      @confirm="confirmDeactivate"
    />
  </BaseDashboardPage>
</template>

<style lang="scss" scoped>
.deactivate-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: rgba(244, 67, 54, 0.1);
  border: 1px solid rgba(244, 67, 54, 0.2);
  border-radius: 10px;
  color: #ef5350;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(244, 67, 54, 0.18);
    border-color: rgba(244, 67, 54, 0.35);
  }
}

.back-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: rgba(15, 23, 48, 0.4);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 10px;
  color: rgba(200, 218, 255, 0.7);
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(15, 23, 48, 0.6);
    border-color: rgba(120, 163, 255, 0.2);
    color: rgba(255, 255, 255, 0.9);
  }
}

/* User Badges */
.user-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 20px;
}

.badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 12px;
  border-radius: 16px;
  font-size: 0.75rem;
  font-weight: 500;
}

.badge-active {
  background: rgba(76, 175, 80, 0.1);
  color: #66bb6a;
  border: 1px solid rgba(76, 175, 80, 0.2);
}

.badge-inactive {
  background: rgba(244, 67, 54, 0.1);
  color: #ef5350;
  border: 1px solid rgba(244, 67, 54, 0.2);
}

.badge-verified {
  background: rgba(0, 176, 255, 0.08);
  color: #4fc3f7;
  border: 1px solid rgba(0, 176, 255, 0.15);
}

.badge-2fa {
  background: rgba(156, 39, 176, 0.08);
  color: #ce93d8;
  border: 1px solid rgba(156, 39, 176, 0.15);
}

.badge-type {
  background: rgba(255, 152, 0, 0.08);
  color: #ffb74d;
  border: 1px solid rgba(255, 152, 0, 0.15);
  text-transform: capitalize;
}

/* Tabs */
.tabs-container {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
}

.tab-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: rgba(15, 23, 48, 0.4);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 10px;
  color: rgba(200, 218, 255, 0.7);
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(15, 23, 48, 0.6);
    border-color: rgba(120, 163, 255, 0.2);
    color: rgba(255, 255, 255, 0.9);
  }

  &.active {
    background: rgba(0, 176, 255, 0.1);
    border-color: rgba(0, 176, 255, 0.3);
    color: #4fc3f7;
  }
}

/* Tab Content */
.tab-content {
  min-height: 200px;
}

.tab-panel {
  animation: fadeIn 0.15s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* Overview Card */
.overview-card {
  background: rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(120, 163, 255, 0.06);
  border-radius: 14px;
  padding: 24px;
}

.overview-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
}

.overview-title {
  font-size: 1rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  margin: 0;
}

.edit-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  background: rgba(0, 176, 255, 0.08);
  color: #4fc3f7;
  border: 1px solid rgba(0, 176, 255, 0.15);
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(0, 176, 255, 0.15);
  }
}

.info-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.info-group {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.info-label {
  font-size: 0.75rem;
  font-weight: 500;
  color: rgba(200, 218, 255, 0.4);
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.info-value {
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.85);
}

.info-capitalize {
  text-transform: capitalize;
}

/* Light Theme */
.v-theme--lightTheme {
  .deactivate-btn {
    background: rgba(244, 67, 54, 0.06);
    border-color: rgba(244, 67, 54, 0.2);
    color: #d32f2f;

    &:hover {
      background: rgba(244, 67, 54, 0.12);
      border-color: rgba(244, 67, 54, 0.3);
    }
  }

  .back-btn {
    background: rgba(0, 0, 0, 0.03);
    border-color: rgba(28, 110, 227, 0.1);
    color: rgba(15, 23, 42, 0.6);

    &:hover {
      background: rgba(0, 0, 0, 0.06);
      border-color: rgba(28, 110, 227, 0.2);
      color: rgba(15, 23, 42, 0.9);
    }
  }

  .tab-btn {
    background: rgba(0, 0, 0, 0.03);
    border-color: rgba(28, 110, 227, 0.08);
    color: rgba(15, 23, 42, 0.6);

    &:hover {
      background: rgba(0, 0, 0, 0.06);
      border-color: rgba(28, 110, 227, 0.15);
      color: rgba(15, 23, 42, 0.9);
    }

    &.active {
      background: rgba(28, 110, 227, 0.08);
      border-color: rgba(28, 110, 227, 0.25);
      color: #1c6ee3;
    }
  }

  .overview-card {
    background: rgba(0, 0, 0, 0.02);
    border-color: rgba(28, 110, 227, 0.06);
  }

  .overview-title {
    color: rgba(15, 23, 42, 0.9);
  }

  .info-label {
    color: rgba(15, 23, 42, 0.4);
  }

  .info-value {
    color: rgba(15, 23, 42, 0.85);
  }

  .edit-btn {
    background: rgba(28, 110, 227, 0.08);
    color: #1c6ee3;
    border-color: rgba(28, 110, 227, 0.15);

    &:hover {
      background: rgba(28, 110, 227, 0.15);
    }
  }
}

/* Responsive */
@media (max-width: 768px) {
  .tabs-container {
    gap: 6px;
  }

  .tab-btn {
    padding: 8px 12px;
    font-size: 0.85rem;
  }

  .info-grid {
    grid-template-columns: 1fr;
  }

  .overview-card {
    padding: 18px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .back-btn,
  .tab-btn,
  .edit-btn,
  .deactivate-btn {
    transition: none;
  }

  .tab-panel {
    animation: none;
  }
}
</style>
